// docs/120 VM2: every player's figures where they are, in every view. My client tells the module where
// each of my figures is (`village_figures`): the rest of its walk from where it stands now, its pace,
// which way it will face and whether it will sit, and what it is doing in the inn. Every other view
// plays each walk on from the moment the row was written, so a band is seen mid-stride, at the same
// place, in every window (the street and the rooms are one size for everyone, docs/120 VM5).
import type { VillageFigure } from '../../../bindings/types';
import type { Room, Station } from './layout';
import { lengthOf, pointAt, type Pt, type WalkGrid } from './walk';
import { AMBLE, wanderOf, wanderStep } from './scenery';
import { sliceLine, type Motion, type Plan } from './motion';
import type { HeroAct } from './inn/store';
import { DRINKS, type DrinkKind } from './inn/types';

/** What a figure is doing, as the module stores it (0 = nothing). */
export const ACTS: readonly HeroAct[] = ['warm', 'poke', 'read', 'gaze', 'drink', 'toast', 'pet', 'throw', 'sleep', 'cheer', 'clap', 'watch'];
const actCode = (a: HeroAct | undefined) => (a ? ACTS.indexOf(a) + 1 : 0);
export const actOf = (n: number): HeroAct | undefined => ACTS[n - 1];
export const heldOf = (n: number): DrinkKind | undefined => DRINKS[n - 1];

/** A figure's key in the view's motion engine, from its hero (and whether it is the hero's beast). */
export const figureKey = (heroId: bigint, beast: boolean) => `h:${heroId}${beast ? '-beast' : ''}`;

/** A plan as the module stores it. */
export function toFigure(heroId: bigint, beast: boolean, p: Plan, act?: HeroAct, held?: DrinkKind): VillageFigure {
  const r = (v: number) => Math.round(v * 1000) / 1000;
  return {
    heroId, beast,
    line: p.line.flatMap((q) => [r(q.x), r(q.y)]),
    s: 0, to: r(p.to), speed: r(p.speed),
    face: p.face, seated: p.seated,
    act: actCode(act), held: held ? DRINKS.indexOf(held) + 1 : 0,
  };
}

export const lineOf = (f: VillageFigure): Pt[] => {
  const out: Pt[] = [];
  for (let i = 0; i + 1 < f.line.length; i += 2) out.push({ x: f.line[i]!, y: f.line[i + 1]! });
  return out;
};

/** Where a figure is `secs` after its row was written, and the rest of its walk from there (none: it has arrived). */
export function figureAt(f: VillageFigure, secs: number): { at: Pt; rest?: Pt[]; done: boolean } {
  const line = lineOf(f);
  const total = Math.min(f.to, lengthOf(line));
  const s = Math.min(total, f.s + Math.max(0, secs) * f.speed);
  const p = pointAt(line, s);
  if (line.length < 2 || s >= total - 1e-4 || f.speed <= 0) return { at: { x: p.x, y: p.y }, done: true };
  return { at: { x: p.x, y: p.y }, rest: sliceLine(line, s, total), done: false };
}

/** How far a figure in my view may be from where another view says it is before it is put there, not walked there. */
const JUMP = 2.5;

/**
 * Bring a figure in my view to where its owner's row says it is `secs` after the row was written:
 * walk on along the rest of its walk (from where my view has it, if near), or put it where it stands.
 * A figure my view has not placed yet appears where it is.
 */
export function replay(m: Motion, key: string, f: VillageFigure, secs: number, speed: number): void {
  const { at, rest, done } = figureAt(f, secs);
  const face = (f.face < 0 ? -1 : 1) as 1 | -1;
  const cur = m.at(key);
  const near = cur && Math.hypot(cur.x - at.x, cur.y - at.y) < JUMP;
  if (!done && rest) {
    if (!near) m.place(key, at, face);
    const line = near ? [{ x: cur!.x, y: cur!.y }, ...rest.slice(cur && Math.hypot(cur.x - rest[0]!.x, cur.y - rest[0]!.y) < 1e-3 ? 1 : 0)] : rest;
    if (line.length < 2) { m.place(key, rest[rest.length - 1]!, face, f.seated); return; }
    m.walk(key, line, { speed: f.speed, end: { face, seated: f.seated } });
    return;
  }
  if (cur && !cur.seated && Math.hypot(cur.x - at.x, cur.y - at.y) > 0.04 && near && !f.seated) {
    // a little way off: step over rather than jump
    m.walk(key, [{ x: cur.x, y: cur.y }, at], { speed, end: { face } });
    return;
  }
  if (cur && Math.hypot(cur.x - at.x, cur.y - at.y) <= 0.04 && !!cur.seated === f.seated && cur.face === face && !m.walking(key)) return;
  m.place(key, at, face, f.seated);
}

/** Two people standing this close (cells) overlap. */
export const OVERLAP = { x: 0.55, y: 0.45 };
export const overlaps = (a: Pt, b: Pt) => Math.abs(a.x - b.x) < OVERLAP.x && Math.abs(a.y - b.y) < OVERLAP.y;

/** docs/120 VM4: how seats and stations are numbered in `village_seat` (the base, plus the index in its list). */
export const SEAT = { table: 100, armchair: 200, hearth: 300, window: 400, bar: 500, dice: 600, bed: 700 } as const;

/** Every seat and station of a room that one person holds at a time, by code. */
export function seatsOf(room: Room, tableSeats: readonly Pt[]): Map<number, Station> {
  const out = new Map<number, Station>();
  tableSeats.forEach((st, i) => out.set(SEAT.table + i, { ...st, seated: true }));
  const p = room.inn;
  if (p) {
    p.armchairs.forEach((st, i) => out.set(SEAT.armchair + i, st));
    p.hearth.forEach((st, i) => out.set(SEAT.hearth + i, st));
    p.gaze.forEach((st, i) => out.set(SEAT.window + i, st));
    p.bar.stand.forEach((st, i) => out.set(SEAT.bar + i, st));
    p.beds.forEach((b, i) => out.set(SEAT.bed + i, b.sleeper));
  }
  return out;
}

/** The code of the seat or station a figure is sent to (none: an ordinary place on the floor). */
export function codeOf(seats: ReadonlyMap<number, Station>, st: Pt): number | undefined {
  for (const [code, s] of seats) if (Math.abs(s.x - st.x) < 0.05 && Math.abs(s.y - st.y) < 0.05) return code;
  return undefined;
}

/**
 * docs/120 VM2: a villager's amble at World time `t` (s): the `n`th step of their round, from where the
 * last one ended to where this one goes, `into` seconds ago, at `pace` give or take, facing `face` at the
 * end. The same seed, villager and time give the same step in every view (docs/121 `wanderOf`).
 */
export function ambleOf(seed: number, key: string, home: Pt, g: WalkGrid, t: number, pace = 0.5): { n: number; from: Pt; to: Pt; into: number; speed: number; face: 1 | -1 } {
  return wanderOf(seed, key, home, g, t, { ...AMBLE, pace });
}

/** Which step of their round a villager is on at `t` (cheap: the frame loop asks every frame), and how far into it. */
export const ambleStep = (seed: number, key: string, t: number): { n: number; into: number } => wanderStep(seed, key, t, AMBLE);
